import type {
  SidebarSearchGroupSource,
  SidebarSearchSources,
} from "./sidebar-search-contract";
import { PROJECT_GROUP_TYPES, type ProjectGroupType } from "./types";

type SidebarSearchItem = {
  id: string;
  label: string;
};

type SidebarSearchProject = {
  id: string;
  label: string;
  items: readonly SidebarSearchItem[];
};

type SidebarSearchGroup = {
  id: ProjectGroupType;
  label: string;
  projects: readonly SidebarSearchProject[];
};

export type SidebarSearchTree = {
  groups: readonly SidebarSearchGroup[];
};

export type SidebarSearchDiagnostic = {
  groupType: ProjectGroupType;
  phase: "index";
  reason:
    | "missing-group-label"
    | "missing-project-id"
    | "missing-project-label"
    | "duplicate-project-id"
    | "missing-item-id"
    | "missing-item-label"
    | "duplicate-item-id";
};

type SidebarSearchAdapterResult = {
  group: SidebarSearchGroup | null;
  diagnostics: readonly SidebarSearchDiagnostic[];
};

export type SidebarSearchGroupAdapter = (
  source: SidebarSearchGroupSource,
) => SidebarSearchAdapterResult;

export type SidebarSearchProjectMatch = {
  id: string;
  itemIds: readonly string[];
  includeAllItems: boolean;
};

type SidebarSearchGroupMatch = {
  id: ProjectGroupType;
  projects: readonly SidebarSearchProjectMatch[];
};

export type SidebarSearchResult = {
  query: string;
  groups: readonly SidebarSearchGroupMatch[];
};

const nonEmptyText = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text ? text : null;
};

const adaptSidebarSearchGroup = (
  groupType: ProjectGroupType,
  source: SidebarSearchGroupSource,
): SidebarSearchAdapterResult => {
  if (!source.isVisible) {
    return { group: null, diagnostics: [] };
  }

  const diagnostics: SidebarSearchDiagnostic[] = [];
  const groupLabel = nonEmptyText(source.label);
  if (!groupLabel) {
    return {
      group: null,
      diagnostics: [
        { groupType, phase: "index", reason: "missing-group-label" },
      ],
    };
  }
  const projectIds = new Set<string>();
  const projects: SidebarSearchProject[] = [];

  for (const projectSource of source.projects) {
    const projectId = nonEmptyText(projectSource.id);
    if (!projectId) {
      diagnostics.push({
        groupType,
        phase: "index",
        reason: "missing-project-id",
      });
      continue;
    }
    const projectLabel = nonEmptyText(projectSource.label);
    if (!projectLabel) {
      diagnostics.push({
        groupType,
        phase: "index",
        reason: "missing-project-label",
      });
      continue;
    }
    if (projectIds.has(projectId)) {
      diagnostics.push({
        groupType,
        phase: "index",
        reason: "duplicate-project-id",
      });
      continue;
    }
    projectIds.add(projectId);

    const itemIds = new Set<string>();
    const items: SidebarSearchItem[] = [];
    for (const itemSource of projectSource.items) {
      const itemId = nonEmptyText(itemSource.id);
      if (!itemId) {
        diagnostics.push({
          groupType,
          phase: "index",
          reason: "missing-item-id",
        });
        continue;
      }
      const itemLabel = nonEmptyText(itemSource.label);
      if (!itemLabel) {
        diagnostics.push({
          groupType,
          phase: "index",
          reason: "missing-item-label",
        });
        continue;
      }
      if (itemIds.has(itemId)) {
        diagnostics.push({
          groupType,
          phase: "index",
          reason: "duplicate-item-id",
        });
        continue;
      }
      itemIds.add(itemId);
      items.push({ id: itemId, label: itemLabel });
    }

    projects.push({ id: projectId, label: projectLabel, items });
  }

  return {
    group: {
      id: groupType,
      label: groupLabel,
      projects,
    },
    diagnostics,
  };
};

export const SIDEBAR_SEARCH_GROUP_ADAPTERS = {
  dashboards: (source) => adaptSidebarSearchGroup("dashboards", source),
  iw: (source) => adaptSidebarSearchGroup("iw", source),
  sa: (source) => adaptSidebarSearchGroup("sa", source),
  ua: (source) => adaptSidebarSearchGroup("ua", source),
  ads: (source) => adaptSidebarSearchGroup("ads", source),
} satisfies Record<ProjectGroupType, SidebarSearchGroupAdapter>;

/**
 * 権限と feature flag 適用後の入力を、React を含まない検索 tree へ変換します。
 */
export const createSidebarSearchTree = (
  sources: SidebarSearchSources,
): {
  tree: SidebarSearchTree;
  diagnostics: readonly SidebarSearchDiagnostic[];
} => {
  const groups: SidebarSearchGroup[] = [];
  const diagnostics: SidebarSearchDiagnostic[] = [];

  for (const groupType of PROJECT_GROUP_TYPES) {
    const result = SIDEBAR_SEARCH_GROUP_ADAPTERS[groupType](sources[groupType]);
    if (result.group) {
      groups.push(result.group);
    }
    diagnostics.push(...result.diagnostics);
  }

  return { tree: { groups }, diagnostics };
};

const includesQuery = (label: string, query: string) =>
  label.toLowerCase().includes(query);

/**
 * 検索 tree と query だけから、表示する group/project/item ID を導出します。
 */
export const filterSidebarSearchTree = (
  tree: SidebarSearchTree,
  rawQuery: string,
): SidebarSearchResult => {
  const query = rawQuery.trim().toLowerCase();
  const groups: SidebarSearchGroupMatch[] = [];

  for (const group of tree.groups) {
    const groupMatches = query === "" || includesQuery(group.label, query);
    const projects: SidebarSearchProjectMatch[] = [];

    for (const project of group.projects) {
      const projectMatches =
        groupMatches || includesQuery(project.label, query);
      const itemIds = projectMatches
        ? project.items.map((item) => item.id)
        : project.items
            .filter((item) => includesQuery(item.label, query))
            .map((item) => item.id);

      if (projectMatches || itemIds.length > 0) {
        projects.push({
          id: project.id,
          itemIds,
          includeAllItems: projectMatches,
        });
      }
    }

    if (groupMatches || projects.length > 0) {
      groups.push({ id: group.id, projects });
    }
  }

  return { query, groups };
};
